import { type FC, type ComponentProps } from 'react';
import { graphql, useFragment } from 'react-relay';
import { filterFalsy } from 'dibs-ts-utils/exports/filterFalsy.ts';

import {
    diveBackIntoYourFavorites,
    mostRecentlySaved,
    viewYourFavorites,
    viewAll,
} from '../../pdpMessages.tsx';
import { trackCarouselViewAllClick } from '../../tracking/pdpTracking.js';
import { PdpSharedProductTileCarousel } from '../PdpSharedProductTileCarousel/PdpSharedProductTileCarousel.tsx';
import { locations } from '../../../utils/tracking/shared/favoritesTracking.tsx';

import { type PdpSharedRecentlyFavoritedCarousel_viewer$key } from './__generated__/PdpSharedRecentlyFavoritedCarousel_viewer.graphql.ts';
import { type PdpSharedRecentlyFavoritedCarousel_user$key } from './__generated__/PdpSharedRecentlyFavoritedCarousel_user.graphql.ts';

const viewerFragment = graphql`
    fragment PdpSharedRecentlyFavoritedCarousel_viewer on Viewer {
        recentlySavedItems: itemRecentlyFavoritedQuery(userId: $userId, excludeItemId: $itemId) {
            items {
                ...PdpSharedProductTileCarousel_items
                    @arguments(userId: $userId, isTrade: $isTrade, priceBookName: $priceBookName)
            }
            isFallback
        }
        ...PdpSharedProductTileCarousel_viewer @arguments(userId: $userId)
    }
`;

const userFragment = graphql`
    fragment PdpSharedRecentlyFavoritedCarousel_user on User {
        ...PdpSharedProductTileCarousel_user
    }
`;

export const PdpSharedRecentlyFavoritedCarousel: FC<{
    viewer: PdpSharedRecentlyFavoritedCarousel_viewer$key;
    user?: PdpSharedRecentlyFavoritedCarousel_user$key | null;
    currency: string;
    classOverrides?: ComponentProps<typeof PdpSharedProductTileCarousel>['classOverrides'];
}> = ({ viewer: viewerRef, user: userRef = null, currency, classOverrides }) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const user = useFragment(userFragment, userRef);

    const items = viewer?.recentlySavedItems?.items?.filter(filterFalsy) || [];
    const totalResults = viewer?.recentlySavedItems?.items?.length || 0;
    const isFallback = viewer?.recentlySavedItems?.isFallback;

    const trackingList = isFallback
        ? locations.MOST_SAVED_ITEMS
        : locations.RECENTLY_FAVORITED_ITEMS;

    if (!items?.length) {
        return null;
    }

    return (
        <PdpSharedProductTileCarousel
            viewer={viewer}
            items={items}
            user={user}
            title={isFallback ? mostRecentlySaved : diveBackIntoYourFavorites}
            viewAllLink={
                isFallback ? '/collections/most-saved-items/?sort=newest' : '/favorites/items/'
            }
            isViewAllLinkable={false}
            viewAllComponent={isFallback ? viewAll : viewYourFavorites}
            handleViewAllClick={() =>
                trackCarouselViewAllClick({
                    trigger: trackingList,
                })
            }
            currency={currency}
            trackingList={trackingList}
            dataTn="pdp-recently-favorited-items-grid"
            totalResults={totalResults}
            classOverrides={classOverrides}
            carouselCapacity={5}
            showArrows={false}
        />
    );
};
